import { fetchConfig } from './utilities.js';
import { CartAddEvent } from './events.js';

/**
 * Custom element for handling add-to-cart functionality for upsell products
 * @extends {HTMLElement}
 */
class UpsellAddToCartComponent extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');

    if (this.button) {
      this.button.addEventListener('click', this.handleAddToCart.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleAddToCart.bind(this));
    }
  }

  /**
   * Handles the add to cart button click
   * @param {MouseEvent} event - The click event
   */
  async handleAddToCart(event) {
    event.preventDefault();

    const variantId = this.dataset.variantId;
    if (!variantId) {
      console.error('No variant ID provided');
      return;
    }

    // Disable button and show loading state
    this.setLoadingState(true);

    try {
      await this.addToCart(variantId);
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message || 'Error adding to cart');
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Adds the variant to the cart
   * @param {string} variantId - The variant ID to add
   */
  async addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    // Get all cart item components to update their sections
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const response = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });

    const data = await response.json();

    if (data.status) {
      // Error occurred
      throw new Error(data.message || data.description);
    }

    // Success - dispatch cart add event
    document.dispatchEvent(
      new CartAddEvent({}, variantId, {
        source: 'upsell-add-to-cart',
        itemCount: 1,
        productId: data.product_id,
        sections: data.sections,
      })
    );

    // Show success feedback
    this.showSuccess();
  }

  /**
   * Sets the loading state of the button
   * @param {boolean} isLoading - Whether the button is in loading state
   */
  setLoadingState(isLoading) {
    if (!this.button) return;

    this.button.disabled = isLoading;
    
    const textSpan = this.button.querySelector('.upsell-add-to-cart__text');
    const loadingSpan = this.button.querySelector('.upsell-add-to-cart__loading');
    
    if (textSpan && loadingSpan) {
      if (isLoading) {
        textSpan.classList.add('hidden');
        loadingSpan.classList.remove('hidden');
      } else {
        textSpan.classList.remove('hidden');
        loadingSpan.classList.add('hidden');
      }
    }
  }

  /**
   * Shows success feedback
   */
  showSuccess() {
    // todo show modal
  }

  /**
   * Shows error message
   * @param {string} message - The error message
   */
  showError(message) {
    // You can implement a toast notification or inline error display here
    console.error(message);
    alert(message); // Simple fallback - can be replaced with better UI
  }
}

if (!customElements.get('upsell-add-to-cart')) {
  customElements.define('upsell-add-to-cart', UpsellAddToCartComponent);
}

